import { SamplePack } from '@arpeggi/shared'
import { Box, Icons, styled } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC } from 'react'
import { PUBLIC_NOTION_URL } from '../../../__legacy__/components/platform/constants'
import Tabs from '../../../__legacy__/components/platform/new-explore/components/Tabs'
import { useSPCollectionTabContent } from '../hooks'
import { BulletPoint, ExternalLink, Section } from '../primitives'
import { PriceCard } from './PriceCard'

const LICENSE_DETAILS_LINK =
  'https://drive.google.com/file/d/1hpxV6LdxKrpvgB41Ce4WXo4wkzup2Llq/view'
const COMMERCIAL_LICENSE_LINK = '/legal/sound-pack-license-commercial'

const Title: FC<ComponentProps<typeof Box>> = (props) => (
  <Box
    column
    justifyCenter
    css={{ text: '28', textFamily: 'dm-bold', marginBottom: '32px' }}
    {...props}
  >
    {props.children}
  </Box>
)

const BulletPointCommercial: FC = () => (
  <BulletPoint.Root>
    <BulletPoint.Icon as={Icons.IDCard} />
    <BulletPoint.TextContainer>
      <BulletPoint.Title>{`Secure a royalty-free, commercial license to samples in this pack`}</BulletPoint.Title>
      <BulletPoint.BodyText>
        {`Token holders of this pack receive a royalty-free, commercial license to use these samples so long as they hold the token. Read `}
        <ExternalLink
          href={COMMERCIAL_LICENSE_LINK}
          target="_blank"
        >{`Artist Sound Pack License`}</ExternalLink>
        {` or `}
        <ExternalLink href={PUBLIC_NOTION_URL} target="_blank">{`FAQ.`}</ExternalLink>
      </BulletPoint.BodyText>
    </BulletPoint.TextContainer>
  </BulletPoint.Root>
)

const BulletPointDownload: FC = () => (
  <BulletPoint.Root>
    <BulletPoint.Icon as={Icons.FileArrowUp} />
    <BulletPoint.TextContainer>
      <BulletPoint.Title>{`Token-gated sample download & track upload`}</BulletPoint.Title>
      <BulletPoint.BodyText>{`Your token unlocks the download and upload buttons. Only token holders can upload official tracks to the sample pack page.`}</BulletPoint.BodyText>
    </BulletPoint.TextContainer>
  </BulletPoint.Root>
)

const BulletPointGroupChat: FC = () => (
  <BulletPoint.Root>
    <BulletPoint.Icon as={Icons.UserSwitch} />
    <BulletPoint.TextContainer>
      <BulletPoint.Title>{`Private group chat with the original artist`}</BulletPoint.Title>
      <BulletPoint.BodyText>{`Share your music and get feedback from the original artist in a token-gated Discord channel. Collaborate, cross-promote, and connect with other holders.`}</BulletPoint.BodyText>
    </BulletPoint.TextContainer>
  </BulletPoint.Root>
)

const ColumnContainer = styled(Box, {
  paddingTop: '24px',
  flexDirection: 'column',
  display: 'flex',
  '@tablet': { flexDirection: 'row' }
})

export const SPCollectTabContent: FC<{ sp: SamplePack }> = observer(({ sp }) => {
  const { color, showCommercialLicenseCopy } = useSPCollectionTabContent(sp)
  return (
    <Tabs.Content value="#collect">
      <Section.Root>
        <Section.Flare1 css={{ color }} />
        <Section.Flare2 css={{ color }} />
        <Section.Content>
          <ColumnContainer>
            <Section.Column css={{ marginRight: '30px' }} wide>
              <Title>{`Unlock exclusive benefits`}</Title>
              {showCommercialLicenseCopy && <BulletPointCommercial />}
              <BulletPointDownload />
              <BulletPointGroupChat />
            </Section.Column>

            <Section.Column>
              {sp.edition?.hasPresale && (
                <PriceCard sp={sp} isPresale={true} style={{ marginBottom: '16px' }} />
              )}
              <PriceCard sp={sp} />
            </Section.Column>
          </ColumnContainer>
        </Section.Content>
      </Section.Root>
    </Tabs.Content>
  )
})
